import { useState, useCallback, useRef } from "react";
import html2canvas from "html2canvas";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useQueryClient } from "@tanstack/react-query";
import { errorLogger } from "@/lib/errorLogger";
import { consoleCapture } from "@/lib/consoleCapture";
import { networkCapture } from "@/lib/networkCapture";
import { toast } from "sonner";

type ImpactLevel = "critical" | "error" | "warning";

interface SubmitParams {
  description?: string;
  impact?: ImpactLevel;
  screenshotDataUrl?: string | null;
}

const MODULE_PATH_MAP: Record<string, string> = {
  "/warehouse": "warehouse", "/manufacturing": "manufacturing",
  "/quality": "quality", "/npd": "npd", "/purchasing": "purchasing",
  "/crm": "crm", "/admin": "admin", "/equipment": "equipment",
  "/training": "training", "/shipping": "shipping", "/documents": "documents",
  "/suppliers": "suppliers", "/stock-items": "stock_items",
  "/planning": "planning", "/tasks": "tasks", "/messages": "messages",
  "/ai-chat": "ai_chat",
};

function inferModule(): string | null {
  const path = window.location.pathname;
  for (const [prefix, mod] of Object.entries(MODULE_PATH_MAP)) {
    if (path.startsWith(prefix)) return mod;
  }
  if (path === "/") return "dashboard";
  return null;
}

function getDeviceInfo(): Record<string, unknown> {
  const info: Record<string, unknown> = {
    screen_width: window.screen?.width,
    screen_height: window.screen?.height,
    viewport_width: window.innerWidth,
    viewport_height: window.innerHeight,
    pixel_ratio: window.devicePixelRatio,
    language: navigator.language,
    online: navigator.onLine,
    scroll_x: window.scrollX,
    scroll_y: window.scrollY,
  };

  // Chrome-specific memory info
  const perf = performance as any;
  if (perf.memory) {
    info.js_heap_used = perf.memory.usedJSHeapSize;
    info.js_heap_total = perf.memory.totalJSHeapSize;
  }

  // Page load timing
  const nav = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined;
  if (nav) {
    info.page_load_ms = Math.round(nav.loadEventEnd - nav.startTime);
    info.dom_interactive_ms = Math.round(nav.domInteractive - nav.startTime);
  }

  return info;
}

function getQueryErrors(queryClient: ReturnType<typeof useQueryClient>): Array<{ key: string; message: string }> {
  const errors: Array<{ key: string; message: string }> = [];
  const cache = queryClient.getQueryCache();
  for (const query of cache.getAll()) {
    if (query.state.status === "error" && query.state.error) {
      errors.push({
        key: JSON.stringify(query.queryKey).slice(0, 200),
        message: query.state.error instanceof Error ? query.state.error.message : String(query.state.error),
      });
    }
  }
  return errors;
}

function dataUrlToBlob(dataUrl: string): Blob | null {
  if (!dataUrl || !dataUrl.includes(",")) return null;
  try {
    const [header, base64] = dataUrl.split(",");
    const mime = header.match(/:(.*?);/)?.[1] || "image/png";
    const binary = atob(base64);
    const array = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) array[i] = binary.charCodeAt(i);
    return new Blob([array], { type: mime });
  } catch {
    return null;
  }
}

export function useBugReport() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submittingRef = useRef(false);

  const captureScreenshot = useCallback(async (): Promise<string | null> => {
    try {
      const canvas = await html2canvas(document.body, {
        scale: 1,
        backgroundColor: "#ffffff",
        useCORS: true,
        logging: false,
        // Skip the floating buttons themselves from the screenshot
        ignoreElements: (el) => el.getAttribute("data-bug-report") === "true",
      });
      return canvas.toDataURL("image/png");
    } catch (err: unknown) {
      console.error("Screenshot capture failed:", err);
      return null;
    }
  }, []);

  const submitReport = useCallback(async ({ description, impact, screenshotDataUrl }: SubmitParams) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setIsSubmitting(true);

    try {
      // 1. Upload screenshot if provided
      let screenshotUrl: string | null = null;
      if (screenshotDataUrl) {
        const blob = dataUrlToBlob(screenshotDataUrl);
        if (blob) {
          const fileName = `${user?.id || "anon"}_${Date.now()}.png`;
          const { data: uploadData, error: uploadError } = await supabase.storage
            .from("bug-report-screenshots")
            .upload(fileName, blob, { contentType: "image/png" });

          if (!uploadError && uploadData) {
            // Store the path — admin viewer generates signed URLs on demand
            screenshotUrl = uploadData.path;
          }
        }
      }

      // 2. Gather auto-captured diagnostics
      const consoleLog = consoleCapture.getEntries();
      const failedRequests = networkCapture.getEntries();
      const queryErrors = getQueryErrors(queryClient);
      const breadcrumbs = errorLogger.getBreadcrumbs();
      const deviceInfo = getDeviceInfo();

      // 3. Direct insert to error_logs (bypasses rate limiter)
      const { error: insertError } = await supabase.from("error_logs").insert({
        error_type: "user_report",
        severity: impact || "error",
        message: description || "User-reported issue (no description provided)",
        platform: "web",
        user_id: user?.id || null,
        user_name: (user?.user_metadata?.full_name as string) || user?.email || null,
        user_email: user?.email || null,
        page_url: window.location.href,
        module: inferModule(),
        user_agent: navigator.userAgent,
        action_context: description ? `User report: ${description.slice(0, 500)}` : "User report (no description)",
        device_info: deviceInfo as any,
        breadcrumbs: breadcrumbs as any,
        screenshot_url: screenshotUrl,
        request_payload: {
          console_log: consoleLog,
          failed_requests: failedRequests,
          query_errors: queryErrors,
        } as any,
        fingerprint: `user_report_${user?.id}_${Date.now()}`,
      });

      if (insertError) throw insertError;

      toast.success("Report sent — we'll look into it");
      queryClient.invalidateQueries({ queryKey: ["error-logs"] });
    } catch (err: unknown) {
      toast.error("Failed to send report. Please try again.");
      console.error("Bug report submission failed:", err);
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  }, [user, queryClient]);

  return { captureScreenshot, submitReport, isSubmitting };
}
